<script lang="ts">
	import { formatDate } from '../../helper/Parser';
	import { handleStoreError } from '../../stores/GeneralStore';
	import { onMount } from 'svelte';
	import { _, json } from 'svelte-i18n';
	import TinyDatePicker from 'tiny-date-picker';
	import 'tiny-date-picker/tiny-date-picker.min.css';
	import IconButton from '../buttons/IconButton.svelte';

	interface Props {
		id?: string | null;
		propertyName: string;
		disabled?: boolean;
		errors?: any;
		min?: Date | null;
		max?: Date | null;
		showInput?: boolean;
		showPickerButton?: boolean;
		callFunction?: ((object: any) => Promise<any>) | null;
		wrappInOptionalParameter?: boolean;
		allowNull?: boolean;
		showAsModal?: boolean;
		//Date as string in ISO format
		value?: string | null;
		onChange?: (value: string | null) => void;
		onBlur?: () => void;
		onKeyDown?: (e: KeyboardEvent) => void;
	}

	let {
		id = null,
		propertyName,
		disabled = false,
		errors = {},
		min = null,
		max = null,
		showInput = true,
		showPickerButton = true,
		callFunction = null,
		wrappInOptionalParameter = false,
		allowNull = true,
		showAsModal = false,
		value = $bindable(null),
		onChange = undefined,
		onBlur = undefined,
		onKeyDown = undefined
	}: Props = $props();
	//Remember the rest value we can use to rest back to if failed
	let resetValue = value;

	//The internal value that is used
	let internal: string | null = $derived(value == null ? null : formatDate(value));
	let dp: any;
	let isOpen: boolean = false;

	let targetContainer: HTMLDivElement | undefined = $state();

	onMount(() => {
		dp = TinyDatePicker(targetContainer, {
			mode: showAsModal ? 'dp-modal' : 'dp-below',
			hilightedDate: new Date(),
			dayOffset: 1,
			min: min,
			max: max,
			lang: {
				months: $json('calendar.months'),
				today: $_('calendar.lbl_today'),
				clear: $_('calendar.lbl_clear'),
				close: $_('calendar.lbl_close')
			}
		});

		async function handleDatePickerStateUpdate(dp: any) {
			//Value used to update the input field later on if successfull
			let updateValue: string | null = null;

			//Evaluate the right update value.
			if (dp.state.selectedDate != null) {
				updateValue = dp.state.selectedDate.toISOString();
			} else {
				if (min) {
					dp.setState({
						selectedDate: min
					});
					updateValue = min.toISOString();
				} else {
					updateValue = null;
				}
			}

			if (updateValue == value) {
				return;
			}

			//Check if we allow null values. If not, we will reset the value incause itself was not null
			if (!allowNull && value != null && updateValue == null) {
				//Set the value back to the one it was
				dp.setState({
					selectedDate: new Date(value)
				});
				return;
			}

			//Check if we have a call function that we musst call. But if first time we do not.
			if (callFunction) {
				try {
					const dynamicObject: any = {};

					if (wrappInOptionalParameter) {
						dynamicObject[propertyName] = { value: updateValue };
					} else {
						dynamicObject[propertyName] = updateValue;
					}

					await callFunction(dynamicObject);

					value = updateValue;

					resetValue = updateValue;

					//We have to set it manually because the reaction wont trigger
					if (value == updateValue && value != null) {
						internal = formatDate(updateValue!);
					}

					//If we set the change from the cal, we send the event.
					onChange?.(value);

					delete errors[propertyName];
				} catch (error) {
					//Update the state back.
					dp.setState({
						selectedDate: new Date(resetValue!)
					});
					//Reset the input field value
					internal = formatDate(resetValue!);

					//Check if we have an error type
					handleStoreError('general.patch_failed', error);
				}
			} else {
				//We have to set it manually because the reaction wont trigger
				if (updateValue != null && value == updateValue) {
					internal = formatDate(updateValue);
				}

				value = updateValue;

				//If we set the change from the cal, we send the event.
				onChange?.(value);

				delete errors[propertyName];
			}
		}

		var date = value == null ? null : new Date(value);

		dp.setState({
			selectedDate: date
		});

		dp.on({
			open: () => (isOpen = true),
			close: () => (isOpen = false),
			select: (_: any, dp: any) => {
				handleDatePickerStateUpdate(dp);
			}
		});
	});

	function handleBeforeInput(event: InputEvent) {
		const target = event.target as HTMLInputElement;

		//Build the next value
		const nextVal =
			target.value.substring(0, target.selectionStart || 0) +
			(event.data ?? '') +
			target.value.substring(target.selectionEnd || 0);

		if (nextVal.length > 10) {
			event.preventDefault();
			return;
		}

		if (nextVal.match(/^[0-9\.\-\/]*$/g) == null) {
			event.preventDefault();
			return;
		}
	}

	/**
	 * This is the on change funtio of the input field. It must be syncronize with the Date Picker value.
	 * @param event
	 */
	async function handleOnChange(event: Event & { currentTarget: EventTarget & HTMLInputElement }) {
		const target = event.target as HTMLInputElement;
		//Set the internal value
		let inputDate = target.value;

		let datePickerDate: Date | null = null;

		try {
			let parsedDate = parseDateNoneUTC(inputDate);

			if (parsedDate == null || parsedDate.toString() == 'Invalid Date') {
				//Set the date picker date to null
				datePickerDate = null;
			} else {
				if (min && parsedDate < min) {
					datePickerDate = min;
				} else if (max && parsedDate > max) {
					datePickerDate = max;
				} else {
					//Now set the external value
					datePickerDate = parsedDate;
				}
			}
		} catch {
			//Inform the datepicker that we have a null date.
			datePickerDate = null;

			//We have to check if the value was already null, otherwise we must set it manually
			//otherwise the input field has wrong info
			if (value == null) {
				internal = null;
				return;
			}
		}

		//Check if we allow null, if we dont replace with the inital value.
		if (!allowNull && datePickerDate == null && value != null) {
			//Reset the initial value if we do not allow null.
			datePickerDate = new Date(value);
			//We have to set the value back because the set state wont tigger an update if it's the same date.
			internal = formatDate(value);
		}

		//If we allow null, reset the input back as well
		if (allowNull && datePickerDate == null) {
			internal = '';
		}

		//Update the state of the date picker
		//This will trigger the on update function from the date picker. Which
		//will call the on change.
		dp.setState({
			selectedDate: datePickerDate
		});
	}

	// We must parse the date, but we do not have a UTC date.
	function parseDateNoneUTC(inputString: string): Date | null {
		if (inputString.includes('.')) {
			if (!inputString.match(/\d{2}\.\d{2}\.\d{4}/gm)) return null;

			let parts = inputString.split('.');
			// new Date(year, month [, day [, hours[, minutes[, seconds[, ms]]]]])
			return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0])); // Note: months are 0-based
		} else if (inputString.includes('/')) {
			if (!inputString.match(/\d{2}\/\d{2}\/\d{4}/gm)) return null;

			let parts = inputString.split('/');
			// new Date(year, month [, day [, hours[, minutes[, seconds[, ms]]]]])
			return new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1])); // Note: months are 0-based
		} else {
			if (!inputString.match(/\d{4}-\d{2}-\d{2}/gm)) return null;

			let parts = inputString.split('-');
			// new Date(year, month [, day [, hours[, minutes[, seconds[, ms]]]]])
			return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])); // Note: months are 0-based
		}
	}

	function showDatePicker(e: MouseEvent) {
		if (isOpen) {
			dp.close();
		} else {
			dp.open();
		}
	}
</script>

<div>
	<div class="flex items-center h-full">
		{#if showInput}
			<input
				{id}
				data-testid={propertyName}
				type="text"
				autocomplete="off"
				{disabled}
				class:mr-2={showPickerButton}
				onbeforeinput={handleBeforeInput}
				onchange={handleOnChange}
				onblur={onBlur}
				bind:value={internal}
				onkeydown={onKeyDown}
				placeholder={'dd.mm.yyyy'}
			/>
		{/if}

		<div bind:this={targetContainer} id={propertyName} class="h-8"></div>

		{#if showPickerButton}
			<IconButton
				dataTestId={'cal-bt-' + propertyName}
				{disabled}
				size={'small'}
				iconName="calendar"
				hoverText={$_('basic_components.bt_open_calendar')}
				onClick={showDatePicker}
			/>
		{/if}
	</div>
	{#if errors[propertyName]}
		<p class="text-red dark:text-orange text-sm">{errors[propertyName]}</p>
	{/if}
</div>

<style>
	:global(.dp::before) {
		background: #1789c0 !important;
	}

	:global(.dp-current) {
		background: #1789c0 !important;
	}

	:global(.dp button:hover) {
		background: #1789c0 !important;
	}
	:global(.dp-modal) {
		z-index: 999;
	}
</style>
